import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { Logo } from '@/components/brand/Logo';
import { navigationFor } from '@/components/layout/navigation';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';

export function Sidebar({ open }: { open: boolean }) {
  const { user } = useAuth();
  const navigation = useMemo(() => navigationFor(user?.role), [user?.role]);

  return (
    <aside
      className={cn(
        'glass fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r transition-transform duration-300 lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div className="flex h-16 items-center gap-2.5 px-5">
        <Logo size={36} />
        <div className="leading-tight">
          <p className="text-sm font-semibold">Techie Gain</p>
          <p className="text-[11px] text-muted-foreground">Infrastructure Operations Platform</p>
        </div>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-4">
        {navigation.map((section) => (
          <div key={section.title}>
            <p className="px-3 pb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {section.title}
            </p>
            <ul className="space-y-1">
              {section.items.map((item) => (
                <li key={item.label}>
                  {item.enabled ? (
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                          // A 15% tint reads as "slightly different", not as "you are here".
                          // Somebody scanning the sidebar for where they are should find it
                          // without looking twice.
                          isActive
                            ? 'bg-primary font-medium text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                        )
                      }
                    >
                      <item.icon className="size-4" />
                      {item.label}
                    </NavLink>
                  ) : (
                    <span
                      className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground/50"
                      title="Coming soon"
                    >
                      <item.icon className="size-4" />
                      {item.label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
